<script lang="ts">
  import { showAlert } from '../lib/ui-alert';
  import { bulkInvite, type BulkInviteResult } from '../lib/admin-api';
  import Icon from './Icon.svelte';

  let { onClose, onDone }: { onClose: () => void; onDone?: () => void } = $props();

  type Step = 'input' | 'results';

  let step = $state<Step>('input');
  let rawText = $state('');
  let defaultRole = $state('READER');
  let busy = $state(false);
  let results = $state<BulkInviteResult[]>([]);
  let succeeded = $state(0);
  let failed = $state(0);

  /** Парсить текст — кожен рядок email або "email,роль" або CSV-колонки */
  function parseLines(): { email: string; role: string }[] {
    return rawText
      .split(/[\n,;\t]+/)
      .map((s) => s.trim())
      .filter((s) => s.includes('@'))
      .map((s) => {
        // Підтримуємо "email role" або "email,role"
        const parts = s.split(/[\s,;]+/);
        const email = parts[0]!.toLowerCase();
        const maybeRole = parts[1]?.toUpperCase();
        const role = ['ADMIN', 'ANALYST', 'READER'].includes(maybeRole ?? '')
          ? (maybeRole as string)
          : defaultRole;
        return { email, role };
      });
  }

  const parsed = $derived(parseLines());
  const uniqueParsed = $derived(
    parsed.filter((a, i) => parsed.findIndex((b) => b.email === a.email) === i),
  );

  async function onSend() {
    if (uniqueParsed.length === 0) return;
    busy = true;
    try {
      const r = await bulkInvite(uniqueParsed);
      results = r.results;
      succeeded = r.succeeded;
      failed = r.failed;
      step = 'results';
      if (r.succeeded > 0) onDone?.();
    } catch (e) {
      showAlert((e as Error).message);
    } finally {
      busy = false;
    }
  }

  function copyAll() {
    const urls = results
      .filter((r) => r.status === 'ok' && r.acceptUrl)
      .map((r) => `${r.email}: ${r.acceptUrl}`)
      .join('\n');
    navigator.clipboard.writeText(urls).catch(() => {});
  }

  function onKeydown(e: KeyboardEvent) {
    if (e.key === 'Escape') onClose();
  }

  const roleLabel: Record<string, string> = { ADMIN: 'Адмін', ANALYST: 'Аналітик', READER: 'Перегляд' };
</script>

<svelte:window onkeydown={onKeydown} />

<div
  class="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-[2px]"
  role="presentation"
  onclick={onClose}
>
  <div
    class="w-[520px] max-h-[90vh] flex flex-col rounded-2xl border border-[var(--color-border-3)] bg-white shadow-2xl overflow-hidden"
    role="dialog"
    onclick={(e) => e.stopPropagation()}
  >
    <!-- Header -->
    <div class="px-5 py-4 border-b border-[var(--color-border-3)] flex items-center justify-between shrink-0">
      <div>
        <div class="text-sm font-semibold">Масове запрошення</div>
        <div class="text-[10.5px] text-[var(--color-text-muted-2)] mt-0.5">
          {#if step === 'input'}Введіть email-адреси або вставте CSV{:else}Результати відправки{/if}
        </div>
      </div>
      <button type="button" onclick={onClose} class="inline-flex items-center text-[var(--color-text-muted)] hover:text-[var(--color-text)] leading-none"><Icon name="close" size={17} /></button>
    </div>

    {#if step === 'input'}
      <!-- Input step -->
      <div class="px-5 py-4 flex-1 overflow-y-auto space-y-4">
        <!-- Role selector -->
        <div class="flex items-center gap-3">
          <label class="text-xs font-medium text-[var(--color-text-muted)] shrink-0">Роль за замовчуванням:</label>
          <div class="flex gap-1.5">
            {#each ['READER', 'ANALYST', 'ADMIN'] as r}
              <button
                type="button"
                onclick={() => (defaultRole = r)}
                class="rounded-lg px-3 py-1 text-[11px] font-semibold border transition-colors
                  {defaultRole === r
                    ? 'bg-[var(--color-pu)] text-white border-[var(--color-pu)]'
                    : 'bg-white text-[var(--color-text-muted)] border-[var(--color-border-3)] hover:border-[var(--color-pu-2)]'}"
              >{roleLabel[r]}</button>
            {/each}
          </div>
        </div>

        <!-- Textarea -->
        <div>
          <label class="block text-xs font-medium text-[var(--color-text-muted)] mb-1.5">
            Email-адреси (по одному на рядок або через кому)
          </label>
          <textarea
            bind:value={rawText}
            rows={8}
            placeholder="user@example.com&#10;manager@example.com, ANALYST&#10;admin@example.com, ADMIN"
            class="w-full rounded-xl border border-[var(--color-border-3)] px-3.5 py-2.5 text-xs font-mono resize-none focus:outline-none focus:ring-2 focus:ring-[var(--color-pu)]/30"
          ></textarea>
          <div class="text-[10.5px] text-[var(--color-text-muted-2)] mt-1.5">
            Можна додати роль через пробіл: <code class="bg-[var(--color-bg-3)] px-1 rounded">email@example.com ADMIN</code>.
            Якщо не вказано — використовується роль за замовчуванням.
          </div>
        </div>

        <!-- Preview -->
        {#if uniqueParsed.length > 0}
          <div class="rounded-xl border border-[var(--color-border-3)] overflow-hidden">
            <div class="px-3.5 py-2 bg-[var(--color-bg-3)] border-b border-[var(--color-border-3)] text-[10.5px] font-semibold text-[var(--color-text-muted-2)] uppercase tracking-wider">
              Буде запрошено: {uniqueParsed.length}
            </div>
            <div class="max-h-36 overflow-y-auto divide-y divide-[var(--color-border-3)]">
              {#each uniqueParsed as inv}
                <div class="px-3.5 py-1.5 flex items-center justify-between text-xs">
                  <span class="font-mono text-[11px]">{inv.email}</span>
                  <span class="text-[10px] text-[var(--color-text-muted-2)]">{roleLabel[inv.role] ?? inv.role}</span>
                </div>
              {/each}
            </div>
          </div>
        {/if}
      </div>

      <div class="px-5 py-3 border-t border-[var(--color-border-3)] flex gap-2 shrink-0">
        <button
          type="button"
          onclick={onSend}
          disabled={busy || uniqueParsed.length === 0}
          class="flex-1 rounded-xl bg-[var(--color-pu)] text-white py-2 text-[12px] font-semibold hover:opacity-90 disabled:opacity-50"
        >
          {busy ? 'Надсилання…' : `Запросити ${uniqueParsed.length > 0 ? uniqueParsed.length : ''}`}
        </button>
        <button type="button" onclick={onClose} class="rounded-xl border border-[var(--color-border-3)] px-4 py-2 text-[12px] hover:bg-[var(--color-bg-3)]">
          Скасувати
        </button>
      </div>

    {:else}
      <!-- Results step -->
      <div class="px-5 py-4 flex-1 overflow-y-auto space-y-3">
        <!-- Summary -->
        <div class="flex gap-3">
          {#if succeeded > 0}
            <div class="flex-1 rounded-xl bg-emerald-50 border border-emerald-200 px-4 py-3 text-center">
              <div class="text-xl font-bold text-emerald-700">{succeeded}</div>
              <div class="text-[10.5px] text-emerald-600">Запрошено</div>
            </div>
          {/if}
          {#if failed > 0}
            <div class="flex-1 rounded-xl bg-red-50 border border-red-200 px-4 py-3 text-center">
              <div class="text-xl font-bold text-red-600">{failed}</div>
              <div class="text-[10.5px] text-red-500">Помилок</div>
            </div>
          {/if}
        </div>

        <!-- Result rows -->
        <div class="rounded-xl border border-[var(--color-border-3)] overflow-hidden divide-y divide-[var(--color-border-3)]">
          {#each results as r}
            <div class="px-3.5 py-2.5 flex items-start gap-2.5">
              <span class="shrink-0 mt-0.5 inline-flex {r.status === 'ok' ? 'text-emerald-600' : 'text-rose-600'}">
                <Icon name={r.status === 'ok' ? 'statusDone' : 'statusFail'} size={16} />
              </span>
              <div class="flex-1 min-w-0">
                <div class="text-[11.5px] font-mono truncate">{r.email}</div>
                {#if r.status === 'error'}
                  <div class="text-[10px] text-red-600 mt-0.5">
                    {r.code === 'invitation.user_exists' ? 'Користувач вже існує' :
                     r.code === 'invitation.already_open' ? 'Вже є активне запрошення' :
                     r.message ?? r.code}
                  </div>
                {:else if r.acceptUrl}
                  <div class="mt-1 flex items-center gap-1.5">
                    <input
                      type="text"
                      readonly
                      value={r.acceptUrl}
                      class="flex-1 text-[10px] font-mono rounded border border-[var(--color-border-3)] bg-[var(--color-bg-3)] px-2 py-0.5 truncate"
                    />
                    <button
                      type="button"
                      onclick={() => navigator.clipboard.writeText(r.acceptUrl!)}
                      class="shrink-0 text-[10px] px-2 py-0.5 rounded border border-[var(--color-border-3)] hover:bg-[var(--color-bg-3)]"
                    >Копіювати</button>
                  </div>
                {/if}
              </div>
            </div>
          {/each}
        </div>
      </div>

      <div class="px-5 py-3 border-t border-[var(--color-border-3)] flex gap-2 shrink-0">
        {#if succeeded > 0}
          <button
            type="button"
            onclick={copyAll}
            class="inline-flex items-center justify-center gap-1.5 flex-1 rounded-xl border border-[var(--color-border-3)] py-2 text-[12px] font-semibold hover:bg-[var(--color-bg-3)]"
          ><Icon name="copy" size={14} /> Скопіювати всі посилання</button>
        {/if}
        <button type="button" onclick={onClose} class="rounded-xl bg-[var(--color-pu)] text-white px-5 py-2 text-[12px] font-semibold hover:opacity-90">
          Готово
        </button>
      </div>
    {/if}
  </div>
</div>
